import { Activity, CheckCircle2, XCircle } from 'lucide-react';
import { SERVICE_URLS } from '@/lib/api';
import { useLocale } from '@/i18n/locale-provider';
import type { Dictionary } from '@/i18n/dictionaries';
import { usePolling } from '@/lib/use-async';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui';

interface Check {
  status: 'up' | 'down';
  latencyMs: number;
  driver?: string;
  url?: string;
  error?: string;
  httpStatus?: number;
}

interface HealthReport {
  service: string;
  status: string;
  version: string;
  environment: string;
  uptimeSeconds: number;
  checks: Record<string, Check>;
}

interface ServiceHealth {
  name: string;
  url: string;
  reachable: boolean;
  latencyMs: number;
  report: HealthReport | null;
  error?: string;
}

/**
 * Polls each service's own /health endpoint directly from the browser.
 *
 * Going service by service rather than through an aggregator is deliberate: if one
 * service is unreachable the others still report, which is exactly the information an
 * operator needs during an incident.
 */
async function probe(name: string, url: string): Promise<ServiceHealth> {
  const started = performance.now();
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 5000);
    const res = await fetch(`${url}/health`, { signal: controller.signal });
    window.clearTimeout(timer);

    const latencyMs = Math.round(performance.now() - started);
    if (!res.ok) return { name, url, reachable: false, latencyMs, report: null, error: `HTTP ${res.status}` };

    const body = (await res.json()) as { data: HealthReport };
    return { name, url, reachable: true, latencyMs, report: body.data };
  } catch (err) {
    return {
      name, url, reachable: false,
      latencyMs: Math.round(performance.now() - started),
      report: null,
      error: err instanceof Error ? (err.name === 'AbortError' ? 'timed out' : err.message) : 'unreachable',
    };
  }
}

export function SystemHealthPage() {
  const { t, n } = useLocale();
  const health = usePolling(
    () => Promise.all(Object.entries(SERVICE_URLS).map(([name, url]) => probe(name, url))),
    15000,
  );

  if (health.loading && !health.data) return <Spinner label={t.health.probing} />;

  const services = health.data ?? [];
  const healthy = services.filter((s) => s.reachable && s.report?.status === 'ok').length;
  const degraded = services.filter((s) => s.reachable && s.report?.status !== 'ok').length;
  const down = services.filter((s) => !s.reachable).length;

  return (
    <>
      <PageHeader
        title={t.health.title}
        subtitle={t.health.subtitle}
        actions={
          <button type="button" onClick={health.reload} className="btn-secondary">{t.common.refreshNow}</button>
        }
      />

      <div className="mb-6 flex flex-wrap gap-3">
        <Summary tone="good" count={n(healthy)} label={t.health.healthy} />
        {degraded > 0 && <Summary tone="warn" count={n(degraded)} label={t.health.degraded} />}
        {down > 0 && <Summary tone="bad" count={n(down)} label={t.health.unreachable} />}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {services.map((service) => (
          <Card key={service.name}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  {service.reachable && service.report?.status === 'ok' ? (
                    <CheckCircle2 size={17} className="shrink-0 text-success-fg" aria-hidden />
                  ) : (
                    <XCircle size={17} className="shrink-0 text-danger-fg" aria-hidden />
                  )}
                  {service.name}
                </h3>
                <p className="ltr-embed mt-0.5 truncate font-mono text-xs text-mist-500">{service.url}</p>
              </div>
              <Badge tone={!service.reachable ? 'bad' : service.report?.status === 'ok' ? 'good' : 'warn'}>
                {!service.reachable ? t.health.unreachable : (service.report?.status ?? 'unknown')}
              </Badge>
            </div>

            {service.reachable && service.report ? (
              <>
                <dl className="mt-4 grid grid-cols-3 gap-3 text-xs">
                  <Metric label={t.health.roundTrip} value={`${n(service.latencyMs)} ms`} />
                  <Metric label={t.health.uptime} value={formatUptime(service.report.uptimeSeconds, t)} />
                  <Metric label={t.health.environment} value={service.report.environment} />
                </dl>

                <div className="mt-4 space-y-1.5">
                  {Object.entries(service.report.checks).map(([name, check]) => (
                    <div
                      key={name}
                      className="flex items-center justify-between gap-3 rounded-md bg-mist-50 px-3 py-2 text-xs"
                    >
                      <span className="ltr-embed font-medium text-mist-700">
                        {name}
                        {check.driver && <span className="ms-1.5 text-mist-500">({check.driver})</span>}
                      </span>
                      <span className="flex items-center gap-2">
                        {check.error && (
                          <span className="max-w-[14rem] truncate text-danger-fg" title={check.error}>
                            {check.error}
                          </span>
                        )}
                        <span className="tabular text-mist-500">{n(check.latencyMs)} ms</span>
                        <Badge tone={check.status === 'up' ? 'good' : 'bad'}>
                          {check.status === 'up' ? t.health.up : t.health.down}
                        </Badge>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="mt-4 rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger-fg">
                {service.error ?? t.health.noResponse}
              </p>
            )}
          </Card>
        ))}
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-xs text-mist-500">
        <Activity size={12} aria-hidden />
        {t.health.reprobed}
      </p>
    </>
  );
}

function Summary({ tone, count, label }: { tone: 'good' | 'warn' | 'bad'; count: string; label: string }) {
  const classes = {
    good: 'border-success-border bg-success-bg text-success-fg',
    warn: 'border-warning-border bg-warning-bg text-warning-fg',
    bad: 'border-danger-border bg-danger-bg text-danger-fg',
  }[tone];
  return (
    <div className={`rounded-lg border px-4 py-2.5 ${classes}`}>
      <span className="tabular font-display text-xl font-semibold">{count}</span>
      <span className="ms-2 text-sm">{label}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-mist-500">{label}</dt>
      <dd className="tabular mt-0.5 font-medium text-mist-800">{value}</dd>
    </div>
  );
}

/** Uptime uses single-letter unit suffixes, which each language supplies itself. */
function formatUptime(seconds: number, t: Dictionary): string {
  const u = t.health.units;
  if (seconds < 60) return `${seconds}${u.second}`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}${u.minute}`;
  if (seconds < 86_400) {
    return `${Math.floor(seconds / 3600)}${u.hour} ${Math.floor((seconds % 3600) / 60)}${u.minute}`;
  }
  return `${Math.floor(seconds / 86_400)}${u.day} ${Math.floor((seconds % 86_400) / 3600)}${u.hour}`;
}
